import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { OnboardingPayloadData, useOnboardingContext } from '@/contexts/onboardingContext';
import useTranslate from '@/hooks/useTranslate';
import { useInsuranceProviders } from '@/pages/members/hooks/useInsuranceProviders';
import { STEPS_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import StepComponent from '@/pages/members/pages/Onboarding/steps/StepComponent';
import { StepProps } from '@/pages/members/pages/Onboarding/steps/types';
import Alert from '@/ui/members/components/Alert';
import FieldError from '@/ui/members/components/form/FieldError';
import InputField from '@/ui/members/components/form/Input';
import InputGroup from '@/ui/members/components/form/InputGroup';
import SelectDialog from '@/ui/members/components/form/SelectDialog';
import Spinner from '@/ui/members/components/Spinner';
import Tooltip from '@/ui/members/components/Tooltip';
import Heading from '@/ui/members/components/typography/Heading';

import getSubmittedData from '../../utils/getSubmittedData';

interface InsuranceProviderFormValues extends OnboardingPayloadData {
  customInsuranceProvider?: string;
  insuranceProvider: string;
}

interface Props extends StepProps {
  /**
   * Whether to show all content or a minimal version
   */
  showAllContent?: boolean;
}

const OTHER_VALUE = 'other';

const InsuranceProviderSelectionStep = ({ nodeType, showAllContent = true, uid }: Props) => {
  const {
    data: { jurisdiction },
    steps
  } = useOnboardingContext();
  const [shouldFetchMoreProviders, setShouldFetchMoreProviders] = useState(false);
  const { t } = useTranslate(`${STEPS_PATH_TRANSLATION_KEY}.InsuranceTypeStep`);

  const extraOptions = useMemo(
    () => [
      { label: t(OTHER_VALUE), value: OTHER_VALUE, visible: true },
      { label: t('idk'), value: "I don't know", visible: true }
    ],
    [t]
  );

  const {
    data: insuranceProvidersResponse,
    isLoading: isLoadingInsuranceProviders,
    isFetching: isFetchingInsuranceProviders,
    isError: isErrorInsuranceProviders
  } = useInsuranceProviders(
    jurisdiction
      ? {
          jurisdiction: jurisdiction as string
        }
      : {}
  );

  const {
    data: moreInsuranceProvidersResponse,
    isLoading: isLoadingMoreInsuranceProviders,
    isFetching: isFetchingMoreInsuranceProviders,
    isError: isErrorMoreInsuranceProviders
  } = useInsuranceProviders({
    exclude_jurisdiction: jurisdiction as string
  });

  const isLoadingProviders = isLoadingInsuranceProviders || isLoadingMoreInsuranceProviders;
  const isFetchingProviders = isFetchingInsuranceProviders || isFetchingMoreInsuranceProviders;
  const isErrorProviders = isErrorInsuranceProviders || isErrorMoreInsuranceProviders;

  const formattedInsuranceProviders = useMemo(() => {
    const providers = insuranceProvidersResponse?.map((provider) => {
      return {
        label: provider.external_name,
        value: provider.external_name,
        visible: true
      };
    });

    if (!jurisdiction) {
      return providers?.concat(extraOptions) || [];
    }

    const moreProviders =
      moreInsuranceProvidersResponse?.map((provider) => {
        return {
          label: provider.external_name,
          value: provider.external_name,
          visible: shouldFetchMoreProviders
        };
      }) || [];

    return providers?.concat(moreProviders).concat(extraOptions) || [];
  }, [
    insuranceProvidersResponse,
    jurisdiction,
    moreInsuranceProvidersResponse,
    extraOptions,
    shouldFetchMoreProviders
  ]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch
  } = useForm<InsuranceProviderFormValues>({
    mode: 'onSubmit',
    defaultValues: (getSubmittedData(nodeType, uid, steps) as InsuranceProviderFormValues) || {
      customInsuranceProvider: '',
      insuranceProvider: ''
    }
  });

  const values = watch();

  return (
    <StepComponent handleSubmit={handleSubmit} uid={uid} nodeType={nodeType} isValid={isValid}>
      {isErrorProviders && (
        <Alert level="error">
          <p>{t('errorFetchingProviders')}</p>
        </Alert>
      )}

      <div className="text-center mb-4">
        <Heading value={1} className="mb-2">
          {t('whoIsYourProviderHeader')}
        </Heading>
        {showAllContent && <p className="text-base mb-3">{t('providerSelectionIntroMessage')}</p>}
      </div>

      <div className="py-0.5">
        <InputGroup>
          <Controller
            defaultValue=""
            name="insuranceProvider"
            control={control}
            rules={{ required: t('insuranceProviderRequired') }}
            render={({ field }) => (
              <>
                {isLoadingProviders ? (
                  <Spinner />
                ) : (
                  <SelectDialog
                    defaultOpen={shouldFetchMoreProviders}
                    id="insurance-provider"
                    isLoading={isFetchingProviders}
                    label={t('insuranceProviderLabel')}
                    onChange={field.onChange}
                    options={formattedInsuranceProviders}
                    placeholder={t('insuranceProviderPlaceholder')}
                    seeMore={{
                      callback: () => setShouldFetchMoreProviders(true),
                      isVisible: !shouldFetchMoreProviders,
                      text: t('seeMoreProviders')
                    }}
                    selected={field.value}
                  />
                )}
              </>
            )}
          />
          <FieldError error={errors.insuranceProvider} />
        </InputGroup>

        {values.insuranceProvider === OTHER_VALUE && (
          <InputGroup>
            <Controller
              name="customInsuranceProvider"
              control={control}
              rules={{ required: t('customInsuranceRequired') }}
              render={({ field }) => (
                <InputField
                  id="custom-insurance-provider"
                  label={t('customInsuranceLabel')}
                  onChange={field.onChange}
                  placeholder={t('customInsurancePlaceholder')}
                  type="text"
                  value={field.value}
                />
              )}
            />
            <FieldError error={errors.customInsuranceProvider} />
          </InputGroup>
        )}

        {values.insuranceProvider !== OTHER_VALUE && (
          <Tooltip tooltipContent={t('tooltipText')}>
            <p className="flex items-center justify-center text-sm text-uniform-gray-300">
              {t('cantFindInsurance')} <FontAwesomeIcon className="ml-2" icon={faInfoCircle} />
            </p>
          </Tooltip>
        )}
      </div>
    </StepComponent>
  );
};

export default InsuranceProviderSelectionStep;
